import type { Rectangle } from 'pixi.js';
import {
    DEFAULT_TEXT_ALPHA,
    getTotalBounds
} from '../textMetricsHelpers';
import {
    DynamicXDirection,
    DynamicYDirection
} from '../../../enums';
import { rgbArray2String, string2rgbaArray } from '../../../libs/videoData';
import type { RgbaString } from '../../../types';
import { Config } from '../../../video-data/config';
import type {
    CanvasTextDynamicBoxBaseRendererSettings,
    Padding
} from './CanvasTextDynamicBoxBase';
import { CanvasTextDynamicBoxBaseRenderer } from './CanvasTextDynamicBoxBase';
import type { CanvasTextDynamicGraphicAnimationRenderer } from './CanvasTextDynamicGraphicAnimation';


type Radii = [number, number, number, number];

export interface CanvasTextDynamicBoxRendererSettings extends CanvasTextDynamicBoxBaseRendererSettings {
    type: typeof Config.BOX_GRAPHIC;
    // rounded corners
    topLeftRadius: number;
    topRightRadius: number;
    bottomRightRadius: number;
    bottomLeftRadius: number;

    // padding
    paddingTop: number;
    paddingBottom: number;
    paddingLeft: number;
    paddingRight: number;

    // animation
    xDirection: DynamicXDirection;
    yDirection: DynamicYDirection;
    xProgress: number;
    yProgress: number;
}

type AnimatableBoxProperties = typeof CanvasTextDynamicBoxRenderer.animatableProperties[number];
export enum AnimatableNonGeometricBoxProperties {
    xProgress = 'xProgress',
    yProgress = 'yProgress'
}


/**
 * CanvasTextDynamicBox is a class that renders a rounded rectangle behind a block of text.
 */
export class CanvasTextDynamicBoxRenderer extends CanvasTextDynamicBoxBaseRenderer {
    override readonly type = Config.BOX_GRAPHIC;
    topLeftRadius = 0;
    topRightRadius = 0;
    bottomRightRadius = 0;
    bottomLeftRadius = 0;
    paddingTop = 0;
    paddingBottom = 0;
    paddingLeft = 0;
    paddingRight = 0;
    xDirection = DynamicXDirection.left;
    yDirection = DynamicYDirection.top;
    xProgress = 1.0;
    yProgress = 1.0;

    override animationConfigurations: CanvasTextDynamicGraphicAnimationRenderer<AnimatableBoxProperties>[] = [];

    override apply(settings: CanvasTextDynamicBoxRendererSettings) {
        super.apply(settings);
        this.topLeftRadius = settings.topLeftRadius;
        this.topRightRadius = settings.topRightRadius;
        this.bottomLeftRadius = settings.bottomLeftRadius;
        this.bottomRightRadius = settings.bottomRightRadius;
        this.paddingTop = settings.paddingTop;
        this.paddingBottom = settings.paddingBottom;
        this.paddingLeft = settings.paddingLeft;
        this.paddingRight = settings.paddingRight;
        this.xDirection = settings.xDirection;
        this.yDirection = settings.yDirection;
        this.xProgress = settings.xProgress;
        this.yProgress = settings.yProgress;
    }

    // Scales each radii in the array to ensure the sum of radii is not over the maximum value.
    #clampRadii(radii: number[], max: number): Radii {
        const total = radii.reduce((sum: number, r: number) => sum + r, 0);
        return (total > max ? radii.map((r: number) => r / total * max) : radii) as Radii;
    }

    /**
     * Returns border radii (scaled if necessary so that the radii fit within the bounds).
     */
    #getClampedRadiiForBounds(bounds: Rectangle): Radii {
        const { width, height } = bounds;
        let {
            topLeftRadius,
            topRightRadius,
            bottomRightRadius,
            bottomLeftRadius,
        } = this;

        // Scale radii based on width.
        // Each adjacent corner pair needs to be clamped individually to use the full available space.
        [topLeftRadius, topRightRadius] = this.#clampRadii([topLeftRadius, topRightRadius], width);
        [bottomLeftRadius, bottomRightRadius] = this.#clampRadii([bottomLeftRadius, bottomRightRadius], width);

        // Scale radii based on height
        [topLeftRadius, bottomLeftRadius] = this.#clampRadii([topLeftRadius, bottomLeftRadius], height);
        [topRightRadius, bottomRightRadius] = this.#clampRadii([topRightRadius, bottomRightRadius], height);

        const radii = [
            topLeftRadius,
            topRightRadius,
            bottomRightRadius,
            bottomLeftRadius,
        ] as Radii;

        return radii;
    }

    override _getPadding(): Padding {
        return {
            top: this.paddingTop,
            bottom: this.paddingBottom,
            left: this.paddingLeft,
            right: this.paddingRight,
        };
    }

    override _getBounds() {
        return getTotalBounds(this._levelBounds);
    }

    /** The list of properties that can be animated during draw */
    static get animatableProperties() {
        return [
            ...CanvasTextDynamicBoxBaseRenderer.baseAnimatableProperties,
            ...Object.values(AnimatableNonGeometricBoxProperties),
        ];
    }

    /** @inheritDoc */
    static get animatableGeometricProperties() {
        return CanvasTextDynamicBoxBaseRenderer.baseAnimatableGeometricProperties;
    }

    override draw(context: CanvasRenderingContext2D) {
        this._levelBounds.forEach((_, i) => {
            const [bounds, fill] = this._applyAnimationToIndex(i);
            const clampedRadii = this.#getClampedRadiiForBounds(bounds);
            drawRoundRect(context, bounds, clampedRadii, fill);
        });
    }

    /**
     * Apply the clipping path for the element at the given index. For example, if this box is for the line level, and
     * there are 3 lines, then index 1 will apply the clipping path for the 2nd line.
     */
    override clipElement(context: CanvasRenderingContext2D, elementIndex: number) {
        const clippingPath = new Path2D();
        const [bounds] = this._applyAnimationToIndex(elementIndex);
        const { x, y, width: w, height: h } = bounds;
        const clampedRadii = this.#getClampedRadiiForBounds(bounds);
        clippingPath.roundRect(x, y, w, h, clampedRadii);
        context.clip(clippingPath);
    }

    /** Apply the animation to the bounds at the given index. This will return a new rectangle and fill color. */
    _applyAnimationToIndex(index: number): [Rectangle, RgbaString] {
        const tempBounds = this._levelBounds[index].clone();

        // apply animation
        let xProgress = this.xProgress;
        let yProgress = this.yProgress;
        let fill = this.fill;
        const [r, g, b] = string2rgbaArray(fill);
        this.animationConfigurations.forEach(animation => {
            switch (animation.property) {
                case 'xProgress': xProgress = animation.computedValues[index]; break;
                case 'yProgress': yProgress = animation.computedValues[index]; break;
                case 'x': {
                    const offset = animation.shouldOffsetPropertyValue ? tempBounds.x : 0;
                    tempBounds.x = animation.computedValues[index] + offset;
                } break;
                case 'y': {
                    const offset = animation.shouldOffsetPropertyValue ? tempBounds.y : 0;
                    tempBounds.y = animation.computedValues[index] + offset;
                } break;
                case 'alpha': fill = rgbArray2String([r, g, b, animation.computedValues[index]]) as RgbaString; break;
            }
        });

        // apply progress
        const xDirection = applyXZigZagToDirection(this.xDirection, index);
        const yDirection = applyYZigZagToDirection(this.yDirection, index);
        applyXProgressToRectangle(tempBounds, xProgress, xDirection);
        applyYProgressToRectangle(tempBounds, yProgress, yDirection);

        return [tempBounds, fill];
    }

    override get _numberOfShapes(): number {
        return this._levelBounds.length;
    }

    override _getOffsetForProperty(property: AnimatableBoxProperties, shouldOffsetPropertyValue: boolean): number {
        let offset = 0;
        switch (property) {
            case 'xProgress':
                if (shouldOffsetPropertyValue) {
                    offset = this.xProgress;
                }
                break;
            case 'yProgress':
                if (shouldOffsetPropertyValue) {
                    offset = this.yProgress;
                }
                break;
            case 'x':
            case 'y':
                // offset is handled during draw
                break;
            case 'alpha':
                if (shouldOffsetPropertyValue) {
                    offset = DEFAULT_TEXT_ALPHA;
                }
                break;
            default:
                throw new Error(`Cannot apply offset to driven range for unsupported property "${property}"`);
        }
        return offset;
    }
}

const applyXZigZagToDirection = (direction: DynamicXDirection, index: number) => {
    switch (direction) {
        case DynamicXDirection.zigZagLeft:
            return index % 2 === 0 ? DynamicXDirection.left : DynamicXDirection.right;
        case DynamicXDirection.zigZagRight:
            return index % 2 === 0 ? DynamicXDirection.right : DynamicXDirection.left;
    }
    return direction;
};

const applyYZigZagToDirection = (direction: DynamicYDirection, index: number) => {
    switch (direction) {
        case DynamicYDirection.zigZagTop:
            return index % 2 === 0 ? DynamicYDirection.top : DynamicYDirection.bottom;
        case DynamicYDirection.zigZagBottom:
            return index % 2 === 0 ? DynamicYDirection.bottom : DynamicYDirection.top;
    }
    return direction;
};

const applyXProgressToRectangle = (rectangle: Rectangle, progress: number, direction: DynamicXDirection) => {
    const { x, width } = rectangle;
    switch (direction) {
        case DynamicXDirection.left:
            rectangle.width = width * progress;
            break;
        case DynamicXDirection.center:
            rectangle.x = x + (width * 0.5) - (width * 0.5 * progress);
            rectangle.width = width * progress;
            break;
        case DynamicXDirection.right:
            rectangle.x = x + width - (width * progress);
            rectangle.width = width * progress;
    }
};

const applyYProgressToRectangle = (rectangle: Rectangle, progress: number, direction: DynamicYDirection) => {
    const { y, height } = rectangle;
    switch (direction) {
        case DynamicYDirection.top:
            rectangle.height = height * progress;
            break;
        case DynamicYDirection.center:
            rectangle.y = y + (height * 0.5) - (height * 0.5 * progress);
            rectangle.height = height * progress;
            break;
        case DynamicYDirection.bottom:
            rectangle.y = y + height - (height * progress);
            rectangle.height = height * progress;
            break;
    }
};

const drawRoundRect = (context: CanvasRenderingContext2D, rectangle: Rectangle, radii: Radii, fill: string) => {
    const { x, y, width, height } = rectangle;
    context.fillStyle = fill;
    context.beginPath();
    context.roundRect(x, y, width, height, radii);
    context.fill();
};
